/**
 * Channels: name rules (the API normalizes with the same function), live validation for the create/rename forms,
 * who may manage a channel (the server enforces the same rules), the browse list, and the optimistic cache edits of
 * channel management (create, settings, join, leave). Pure: no I/O, no clock (callers pass `now`).
 */
import type { Channel, ChannelMembership, CreateChannelRequest, Member, UpdateChannelRequest, UserStatus } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { capabilitiesFor, isExpired, isRestricted } from "./access.ts";
import { memberPaused } from "./dnd.ts";
import { activeStatus } from "./profile.ts";
import { myFeatureAccess } from "./features.ts";
import { agentState, type LiveState } from "./live.ts";
import { matchScore, rank } from "./search.ts";
import { agentPresence, type AvatarPresence } from "./sidebar.ts";
import type { WorkspaceCache } from "./workspace.ts";

export const CHANNEL_NAME_MAX = 80;
export const CHANNEL_TOPIC_MAX = 250;

function isDmKind(kind: string): boolean {
  return kind === "dm" || kind === "group_dm";
}

/** Characters a channel name keeps: letters and digits (any script), "_" and "-". */
function keeps(ch: string): boolean {
  return ch === "_" || ch === "-" || /^[\p{L}\p{N}]$/u.test(ch);
}

function trimDashes(s: string): string {
  let start = 0;
  let end = s.length;
  while (start < end && s[start] === "-") start++;
  while (end > start && s[end - 1] === "-") end--;
  return s.slice(start, end);
}

/** The full normalized name before the length cut (used to tell "too long" apart). */
function normalizeUncut(raw: string): string {
  let s = raw.trim();
  while (s.startsWith("#")) s = s.slice(1);
  s = s.toLowerCase();
  let out = "";
  let pendingDash = false;
  for (const ch of s) {
    if (/^\s$/u.test(ch) || ch === "-") {
      pendingDash = true;
      continue;
    }
    if (!keeps(ch)) continue;
    if (pendingDash && out !== "") out += "-";
    pendingDash = false;
    out += ch;
  }
  return trimDashes(out);
}

/**
 * "  #Q3 Launch!! " → "q3-launch": strips leading "#", lowercases, turns whitespace and dash runs into one dash,
 * drops everything but letters, digits, "_" and "-", trims dashes and cuts at CHANNEL_NAME_MAX. May return "".
 */
export function normalizeChannelName(raw: string): string {
  return trimDashes(normalizeUncut(raw).slice(0, CHANNEL_NAME_MAX));
}

/**
 * Why `raw` can't be a channel name, or undefined when it can: required (nothing left after normalizing), too long
 * (over CHANNEL_NAME_MAX once normalized), or taken (`taken` = names of the channels I know, any case; the server also
 * rejects names of private channels I can't see).
 */
export function channelNameError(raw: string, taken: string[]): MessageDescriptor | undefined {
  const full = normalizeUncut(raw);
  if (full === "") return msg("channels.name.required");
  if (full.length > CHANNEL_NAME_MAX) return msg("channels.name.tooLong", { max: CHANNEL_NAME_MAX });
  const name = normalizeChannelName(raw);
  for (const t of taken) if (t.toLowerCase() === name) return msg("channels.name.taken", { name });
  return undefined;
}

/** "Will be created as #q3-launch" when normalizing changes what was typed; undefined otherwise. */
export function channelNameHint(raw: string): MessageDescriptor | undefined {
  const name = normalizeChannelName(raw);
  const typed = raw.trim();
  if (name === "" || typed === name || typed === `#${name}`) return undefined;
  return msg("channels.name.willBe", { name });
}

/** Names of the public/private channels in the cache, except `exceptId` (the channel being renamed). */
export function knownChannelNames(cache: WorkspaceCache, exceptId: string | undefined): string[] {
  const out: string[] = [];
  for (const id of Object.keys(cache.channels)) {
    const c = cache.channels[id]!;
    if (id !== exceptId && !isDmKind(c.kind)) out.push(c.name);
  }
  return out.toSorted();
}

// ---------- Permissions (hide affordances; the server enforces) ----------

export type ChannelPermissions = {
  isMember: boolean;
  /** Rename, convert public ↔ private: workspace admins/owners or the creator; not archived; not a DM; #general stays public. */
  canRename: boolean;
  canConvert: boolean;
  /** Archive / unarchive: admins/owners or the creator; not a DM; #general can't be archived. */
  canArchive: boolean;
  /** Topic and description: any member while not archived. */
  canEditTopic: boolean;
  /** Leave: members of a channel (not DMs, not #general). */
  canLeave: boolean;
  /** Join: a public, unarchived channel I'm not in (restricted members only join what was shared with them). */
  canJoin: boolean;
};

function isAdmin(m: Member): boolean {
  return (m.role === "owner" || m.role === "admin") && !isRestricted(m);
}

/** May `me` rename / convert / archive `channel`? (admin or owner, or the channel's creator) */
export function canManageChannel(me: Member, channel: Channel): boolean {
  if (isDmKind(channel.kind)) return false;
  return isAdmin(me) || channel.createdBy === me.userId;
}

/**
 * The workspace's #general: nobody leaves it, and it stays public and unarchived. The server checks the default channel
 * it recorded at creation; clients know it as the public channel named "general", which is how it is created.
 */
/**
 * The line under a conversation's intro ("This is the very beginning of #design"): "Send a message to get the
 * conversation going." — but not in an archived channel, where nobody can (Slack). Unknown channel: none.
 */
export function introPrompt(channel: Channel | undefined): MessageDescriptor | undefined {
  if (channel === undefined || channel.archived) return undefined;
  return msg("channel.empty.body");
}

export function isGeneralChannel(channel: Channel): boolean {
  return channel.kind === "public" && channel.name === "general";
}

/** May I leave `channelId`? Channels I'm in, except DMs and #general (the server refuses both). */
export function canLeaveChannel(cache: WorkspaceCache, channelId: string): boolean {
  const c = cache.channels[channelId];
  return c !== undefined && cache.memberships[channelId] !== undefined && !isDmKind(c.kind) && !isGeneralChannel(c);
}

export function channelPermissions(cache: WorkspaceCache, channelId: string, now: number): ChannelPermissions {
  const c = cache.channels[channelId];
  const me = cache.members[cache.meId];
  const none: ChannelPermissions = { isMember: false, canRename: false, canConvert: false, canArchive: false, canEditTopic: false, canLeave: false, canJoin: false };
  if (c === undefined || me === undefined) return none;
  const isMember = cache.memberships[channelId] !== undefined;
  const general = isGeneralChannel(c);
  const manage = canManageChannel(me, c) && !isExpired(me, now);
  return {
    isMember,
    canRename: manage && !c.archived,
    canConvert: manage && !c.archived && !general,
    canArchive: manage && (!general || c.archived),
    canEditTopic: isMember && !c.archived,
    canLeave: canLeaveChannel(cache, channelId),
    canJoin: !isMember && c.kind === "public" && !c.archived && !isRestricted(me),
  };
}

/** May I create public/private channels here? (`can.createChannels`; false once access expired) */
export function canCreateChannels(cache: WorkspaceCache, now: number): boolean {
  const me = cache.members[cache.meId];
  // Not while Chat is off, even for owners and admins who may still look (logic/features).
  return me !== undefined && capabilitiesFor(me, now).createChannels && myFeatureAccess(cache, "chat", now) === "full";
}

// ---------- Browse ----------

export type BrowseFilter = "all" | "joined" | "other" | "archived";

export type BrowseRow = {
  channelId: string;
  name: string;
  isPrivate: boolean;
  archived: boolean;
  memberCount: number;
  topic?: string;
  purpose?: string;
  isMember: boolean;
  canJoin: boolean;
  canLeave: boolean;
};

/**
 * Channels to browse: the cache's public/private channels plus `listed` (the server's browse list, which adds
 * archived channels; cache entries win since realtime keeps them fresh). `filter`: all unarchived, the ones I'm in,
 * the ones I'm not in, or archived only. Ranked by `query` (name, then topic/description), else by name.
 */
export function browseChannels(cache: WorkspaceCache, listed: Channel[], query: string, filter: BrowseFilter): BrowseRow[] {
  const me = cache.members[cache.meId];
  const restricted = me !== undefined && isRestricted(me);
  const all: Record<string, Channel> = {};
  for (const c of listed) all[c.id] = c;
  for (const id of Object.keys(cache.channels)) all[id] = cache.channels[id]!;
  const rows: Record<string, BrowseRow> = {};
  const candidates: { id: string; text: string; alt?: string }[] = [];
  for (const id of Object.keys(all)) {
    const c = all[id]!;
    if (isDmKind(c.kind)) continue;
    const isMember = cache.memberships[id] !== undefined;
    if (c.kind === "private" && !isMember) continue;
    if (filter === "archived" ? !c.archived : c.archived) continue;
    if (filter === "joined" && !isMember) continue;
    if (filter === "other" && isMember) continue;
    const row: BrowseRow = {
      channelId: id,
      name: c.name,
      isPrivate: c.kind === "private",
      archived: c.archived,
      memberCount: c.memberCount,
      isMember,
      canJoin: !isMember && c.kind === "public" && !c.archived && !restricted,
      canLeave: isMember && !isGeneralChannel(c),
    };
    if (c.topic !== undefined && c.topic !== "") row.topic = c.topic;
    if (c.purpose !== undefined && c.purpose !== "") row.purpose = c.purpose;
    rows[id] = row;
    const alt = [row.topic, row.purpose].filter((x) => x !== undefined).join(" ");
    candidates.push(alt === "" ? { id, text: c.name } : { id, text: c.name, alt });
  }
  return rank(query, candidates, candidates.length).map((id) => rows[id]!);
}

// ---------- Optimistic cache edits ----------

/** The channel a CreateChannelRequest (with its client id) will create, as the server will return it. */
export function optimisticChannel(req: CreateChannelRequest, id: string, meId: string, workspaceId: string, now: number): Channel {
  const others = (req.memberIds ?? []).filter((u, i, a) => u !== meId && a.indexOf(u) === i);
  const c: Channel = {
    id,
    workspaceId,
    kind: req.kind,
    name: normalizeChannelName(req.name),
    createdBy: meId,
    createdAt: now,
    archived: false,
    memberCount: 1 + others.length,
    lastMessageSeq: 0,
  };
  if (req.topic !== undefined && req.topic !== "") c.topic = req.topic;
  if (req.purpose !== undefined && req.purpose !== "") c.purpose = req.purpose;
  return c;
}

function freshMembership(c: Channel, meId: string, now: number): ChannelMembership {
  return { channelId: c.id, userId: meId, lastReadSeq: c.lastMessageSeq, mentionCount: 0, notify: "default", muted: false, starred: false, joinedAt: now };
}

/** Puts a channel I belong to into the cache (create / join result); keeps an existing membership. */
export function addChannel(cache: WorkspaceCache, channel: Channel, now: number): WorkspaceCache {
  const channels = { ...cache.channels, [channel.id]: channel };
  if (cache.memberships[channel.id] !== undefined) return { ...cache, channels };
  return { ...cache, channels, memberships: { ...cache.memberships, [channel.id]: freshMembership(channel, cache.meId, now) } };
}

/** Drops a channel and my membership (a create the server refused). */
export function removeChannel(cache: WorkspaceCache, channelId: string): WorkspaceCache {
  if (cache.channels[channelId] === undefined && cache.memberships[channelId] === undefined) return cache;
  const channels = { ...cache.channels };
  delete channels[channelId];
  const memberships = { ...cache.memberships };
  delete memberships[channelId];
  return { ...cache, channels, memberships };
}

/** Applies channel settings locally as the server will (names normalized, empty topic/purpose cleared). */
export function applyChannelUpdate(cache: WorkspaceCache, channelId: string, req: UpdateChannelRequest): WorkspaceCache {
  const c = cache.channels[channelId];
  if (c === undefined) return cache;
  const next: Channel = { ...c };
  if (req.name !== undefined) {
    const name = normalizeChannelName(req.name);
    if (name !== "") next.name = name;
  }
  if (req.topic !== undefined) {
    if (req.topic.trim() === "") delete next.topic;
    else next.topic = req.topic.trim();
  }
  if (req.purpose !== undefined) {
    if (req.purpose.trim() === "") delete next.purpose;
    else next.purpose = req.purpose.trim();
  }
  if (req.kind !== undefined && !isDmKind(c.kind)) next.kind = req.kind;
  // "Convert to private channel": a group DM becomes a named private channel with the same people.
  if (c.kind === "group_dm" && req.kind === "private" && next.name !== "") {
    next.kind = "private";
    delete next.dmUserIds;
  }
  if (req.archived !== undefined) next.archived = req.archived;
  return { ...cache, channels: { ...cache.channels, [channelId]: next } };
}

/** I left: my membership goes; a public channel stays browsable (one member fewer), any other kind disappears. */
export function applyLeave(cache: WorkspaceCache, channelId: string): WorkspaceCache {
  const c = cache.channels[channelId];
  if (cache.memberships[channelId] === undefined) return cache;
  const memberships = { ...cache.memberships };
  delete memberships[channelId];
  const channels = { ...cache.channels };
  if (c !== undefined && c.kind === "public" && !c.archived) channels[channelId] = { ...c, memberCount: Math.max(0, c.memberCount - 1) };
  else delete channels[channelId];
  return { ...cache, channels, memberships };
}

// ---------- People to add ----------

export type MemberOption = { userId: string; name: string; avatarUrl?: string; isAgent: boolean; organization?: string };

/** How many of my conversations `userId` is in, as far as the cache knows (what can.seeDirectory "shared_channels" allows). */
function sharedWith(cache: WorkspaceCache, userId: string): number {
  let n = 0;
  for (const id of Object.keys(cache.memberships)) {
    const c = cache.channels[id];
    if (c === undefined) continue;
    if (isDmKind(c.kind)) {
      if (c.dmUserIds !== undefined && c.dmUserIds.includes(userId)) n++;
    } else n++;
  }
  return n;
}

/**
 * People I can add to a channel: active members I may see (`can.seeDirectory`; the server already limits the
 * directory it sends), except me and `exclude`, matching `query` (name or full name), best match first, at most `limit`.
 */
export function addableMembers(cache: WorkspaceCache, query: string, exclude: string[], now: number, limit: number): MemberOption[] {
  const me = cache.members[cache.meId];
  if (me === undefined) return [];
  const directoryAll = capabilitiesFor(me, now).seeDirectory === "all";
  const byId: Record<string, MemberOption> = {};
  const candidates: { id: string; text: string; alt?: string }[] = [];
  for (const id of Object.keys(cache.members)) {
    const m = cache.members[id]!;
    if (m.deactivated || id === cache.meId || exclude.includes(id) || isExpired(m, now)) continue;
    if (!directoryAll && sharedWith(cache, id) === 0 && me.access?.sponsorId !== id) continue;
    const o: MemberOption = { userId: id, name: m.displayName, isAgent: m.kind === "agent" };
    if (m.avatarUrl !== undefined) o.avatarUrl = m.avatarUrl;
    const org = m.access === undefined || m.access.organization === undefined ? undefined : m.access.organization.name;
    if (org !== undefined) o.organization = org;
    byId[id] = o;
    candidates.push(m.fullName !== m.displayName ? { id, text: m.displayName, alt: m.fullName } : { id, text: m.displayName });
  }
  return rank(query, candidates, limit).map((id) => byId[id]!);
}

// ---------- Channel members (the members panel) ----------

export type ChannelMemberView = {
  userId: string;
  /** Display name (the user id while the profile isn't known yet). */
  name: string;
  /** "@handle" without the "@". */
  handle?: string;
  /** Full name when it differs from the display name. */
  fullName?: string;
  avatarUrl?: string;
  presence?: AvatarPresence;
  /** Do not disturb is on (a moon instead of the presence dot). */
  dnd?: boolean;
  /** Badge: workspace owner / admin, or guest. */
  role?: "owner" | "admin" | "guest";
  isAgent: boolean;
  isMe: boolean;
  /** Another organization ("External · Org"). */
  organization?: string;
  /** Guests and agents: who is accountable for them. */
  sponsorName?: string;
  /** I may remove this person (not myself). */
  canRemove: boolean;
  /** Their current custom status: the emoji after the name (logic/profile activeStatus). */
  status?: UserStatus;
};

export type ChannelMembersPermissions = {
  /** Add people: members of a channel (not a DM, not archived); restricted members need `can.invite`. */
  canAdd: boolean;
  /** Remove people: workspace admins/owners or the creator; never from #general (`isDefault`), DMs or archived channels. */
  canRemove: boolean;
};

export function channelMembersPermissions(cache: WorkspaceCache, channelId: string, isDefault: boolean, now: number): ChannelMembersPermissions {
  const c = cache.channels[channelId];
  const me = cache.members[cache.meId];
  if (c === undefined || me === undefined || isDmKind(c.kind) || c.archived || isExpired(me, now)) return { canAdd: false, canRemove: false };
  const isMember = cache.memberships[channelId] !== undefined;
  return {
    canAdd: isMember && (!isRestricted(me) || capabilitiesFor(me, now).invite),
    canRemove: !isDefault && canManageChannel(me, c),
  };
}

function presenceOf(cache: WorkspaceCache, live: LiveState, userId: string): AvatarPresence | undefined {
  const m = cache.members[userId];
  if (m !== undefined && m.kind === "agent") return agentPresence(agentState(live, m).state);
  const p = live.presence[userId];
  return p === "active" ? "online" : p === "away" ? "away" : undefined;
}

/**
 * The members panel: `memberIds` (from the server) as rows, people online first, then by name; `query` matches name,
 * full name or handle. `isDefault` (#general) and my permissions decide who has a Remove action.
 */
export function channelMemberRows(cache: WorkspaceCache, live: LiveState, channelId: string, memberIds: string[], query: string, isDefault: boolean, now: number): ChannelMemberView[] {
  const perms = channelMembersPermissions(cache, channelId, isDefault, now);
  const rows: ChannelMemberView[] = [];
  for (const userId of memberIds) {
    const m = cache.members[userId];
    const row: ChannelMemberView = { userId, name: m === undefined ? userId : m.displayName, isAgent: m !== undefined && m.kind === "agent", isMe: userId === cache.meId, canRemove: perms.canRemove && userId !== cache.meId };
    if (m !== undefined) {
      const handle = handleOf(m);
      if (handle !== undefined) row.handle = handle;
      if (m.fullName !== "" && m.fullName !== m.displayName) row.fullName = m.fullName;
      if (m.avatarUrl !== undefined) row.avatarUrl = m.avatarUrl;
      if (m.role === "guest") row.role = "guest";
      else if ((m.role === "owner" || m.role === "admin") && !isRestricted(m)) row.role = m.role;
      if (m.access !== undefined && m.access.organization !== undefined) row.organization = m.access.organization.name;
      const sponsor = m.access === undefined || m.access.sponsorId === undefined ? undefined : cache.members[m.access.sponsorId];
      if (sponsor !== undefined && (m.kind === "agent" || isRestricted(m))) row.sponsorName = sponsor.displayName;
    }
    const presence = presenceOf(cache, live, userId);
    if (presence !== undefined) row.presence = presence;
    if (m !== undefined && m.kind === "human" && memberPaused(m, now)) row.dnd = true;
    const status = m === undefined ? undefined : activeStatus(m, now);
    if (status !== undefined) row.status = status;
    // The name matches at all, or the full name / handle contains the query.
    const matches = matchScore(query, row.name) > 0 || matchScore(query, row.fullName ?? "") >= 40 || matchScore(query, row.handle ?? "") >= 40;
    if (!matches) continue;
    rows.push(row);
  }
  const online = (r: ChannelMemberView) => (r.presence === "online" ? 0 : 1);
  return rows.toSorted((a, b) => online(a) - online(b) || cmpName(a.name, b.name) || (a.userId < b.userId ? -1 : a.userId > b.userId ? 1 : 0));
}

/** The member's "@handle", when the server sends one (read loosely: older contracts have no `handle`). */
function handleOf(m: Member): string | undefined {
  const h = (m as { handle?: unknown }).handle;
  return typeof h === "string" && h !== "" ? h : undefined;
}

function cmpName(a: string, b: string): number {
  const x = a.toLowerCase();
  const y = b.toLowerCase();
  return x < y ? -1 : x > y ? 1 : 0;
}
